/**
 * CombatSystem — OWNER: combat builder (packages/sim/src/combat/**).
 * Effect DSL, damage/heal pipeline, absorbs, auras, zones, telegraphs, threat + taunt,
 * melee swings and the attack table, CC + DR, resources, deaths. Rules: PLAN.md §3.
 */
import type { CombatSystem, SimContext, UnitId } from '../contracts';
import { applyAura, removeAura, tickAuras } from './auras';
import { tickTelegraphs, tickZones } from './areas';
import { evalCondition } from './conditions';
import { dealDamage, heal } from './damage';
import { resolveDeaths, revive } from './deaths';
import { applyEffects } from './effects';
import { tickMelee } from './melee';
import { tickResources } from './resources';
import { controlState, effectiveStats } from './stats';
import { addHealingThreat, addThreat, pickVictim, reselectVictim, taunt } from './threat';

/** Extra helpers beyond the CombatSystem contract (see contractChangeRequests). */
export interface CombatExtensions {
  /** Pure victim choice: fixate > taunt > threat with the 110% melee / 130% ranged rule. */
  pickVictim(ctx: SimContext, npcId: UnitId): UnitId | null;
  /** Apply victim reselection (every THREAT_RESELECT_MS unless forced); sets victimId/targetId. */
  reselectVictim(ctx: SimContext, npcId: UnitId, force?: boolean): UnitId | null;
}

export function createCombatSystem(): CombatSystem & CombatExtensions {
  return {
    applyEffects,
    dealDamage: (ctx, req) => dealDamage(ctx, req),
    heal: (ctx, req) => heal(ctx, req),
    applyAura: (ctx, targetId, auraId, sourceId, abilityId, opts) => applyAura(ctx, targetId, auraId, sourceId, abilityId, opts),
    removeAura,
    tickAuras,
    tickZones,
    tickTelegraphs,
    tickMelee,
    tickResources,
    resolveDeaths,
    addThreat,
    addHealingThreat,
    taunt: (ctx, npcId, taunterId, durationMs) => {
      taunt(ctx, npcId, taunterId, durationMs);
    },
    effectiveStats,
    controlState,
    revive,
    evalCondition,
    pickVictim,
    reselectVictim,
  };
}

export { computeAmount } from './amount';
export { rollAttackTable } from './damage';
